/**
 * Fila de conquistas desbloqueadas — o fim do unlock silencioso.
 *
 * ## Por que uma fila, e não "abre o modal na hora"
 *
 * O ganho de badge chega em três regimes diferentes e nenhum deles é um bom
 * momento para interromper:
 *
 *  1. `useBadgeAutoUnlock` dispara N callables em paralelo quando a stat muda —
 *     um import de estante concede várias no mesmo tick;
 *  2. o ganho pode cair no meio de uma sessão de Foco, do checkout ou de uma
 *     captura do Studio, onde modal por cima é a lição da família BUG-62/67/68;
 *  3. o app pode ser fechado entre o ganho e a próxima tela segura.
 *
 * Então o unlock só ENFILEIRA. Quem decide o "quando" é `useBadgeUnlockQueue`,
 * e o gate de contexto crítico vive em `celebrationGate`.
 *
 * ## Espelho em localStorage
 *
 * A fila é memória + espelho. Fechar o app no meio do Foco não pode apagar a
 * conquista: ela é o prêmio, e reconceder não acontece (badge é one-way, e o
 * `unlockBadge` devolve `{unlocked:false}` na segunda vez — o ganho seria
 * silencioso para sempre). Drenou, limpa.
 *
 * ## `celebrated` — a trava contra dois modais para o MESMO badge
 *
 * O fluxo de conclusão de livro celebra por conta própria: o
 * `BookCompletionModal` renderiza `newBadges[0]`. Esse id é marcado como
 * celebrado ANTES de qualquer enfileiramento, e o `useBadgeAutoUnlock` — que
 * roda logo em seguida, porque as stats acabaram de mudar — não o repete.
 *
 * ⚠️ Só o PRIMEIRO é reivindicado, porque só o primeiro é renderizado.
 * Decisão do fundador (03/08), delta consciente ao mock: concluir um livro que
 * concede 3 badges mostrava 1 e engolia 2 — que é exatamente o bug em correção.
 * As demais entram na fila e aparecem quando o modal de conclusão fechar.
 */

const QUEUE_KEY = "livra_badge_unlock_queue";
const CELEBRATED_KEY = "livra_badge_unlock_celebrated";

/**
 * Teto do conjunto de celebrados. Ele só cresce (16 badges no catálogo hoje),
 * mas um catálogo futuro não pode transformar o localStorage em depósito.
 */
const CELEBRATED_MAX = 64;

type Listener = (queue: string[]) => void;

let queue: string[] = [];
let celebrated: string[] = [];
let hydrated = false;
const listeners = new Set<Listener>();

function readArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    // Storage indisponível (Safari privado, quota) ou JSON corrompido: a fila
    // degrada para memória pura. Perder o espelho é pior que perder a sessão.
    return [];
  }
}

function writeArray(key: string, value: string[]): void {
  try {
    if (value.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* idem readArray — silencioso de propósito */
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  queue = readArray(QUEUE_KEY);
  celebrated = readArray(CELEBRATED_KEY);
}

function emit(): void {
  const snapshot = [...queue];
  for (const l of listeners) l(snapshot);
}

/** Fila atual, na ordem do ganho. */
export function getBadgeQueue(): string[] {
  hydrate();
  return [...queue];
}

/**
 * Enfileira ids recém-desbloqueados.
 *
 * Ignora o que já está na fila e o que já foi celebrado — o `useBadgeAutoUnlock`
 * pode disparar duas vezes para a mesma stat, e a callable é idempotente, mas a
 * celebração não pode ser.
 */
export function enqueueBadgeUnlocks(badgeIds: string[]): void {
  hydrate();
  const known = new Set([...queue, ...celebrated]);
  const fresh = badgeIds.filter((id) => id && !known.has(id));
  if (fresh.length === 0) return;

  queue = [...queue, ...fresh];
  writeArray(QUEUE_KEY, queue);
  emit();
}

/**
 * Marca ids como já celebrados SEM passar pela fila.
 *
 * Usado pelo fluxo de conclusão de livro, que celebra por conta própria.
 */
export function markBadgesCelebrated(badgeIds: string[]): void {
  hydrate();
  const known = new Set(celebrated);
  const fresh = badgeIds.filter((id) => id && !known.has(id));
  if (fresh.length === 0) return;

  celebrated = [...celebrated, ...fresh].slice(-CELEBRATED_MAX);
  writeArray(CELEBRATED_KEY, celebrated);

  // Um id marcado enquanto já estava na fila sai dela — a autoridade é quem
  // reivindicou. Acontece na corrida entre conclusão e auto-unlock.
  const filtered = queue.filter((id) => !badgeIds.includes(id));
  if (filtered.length !== queue.length) {
    queue = filtered;
    writeArray(QUEUE_KEY, queue);
    emit();
  }
}

/**
 * Remove ids da fila e os registra como celebrados. Chamado ao fechar o cartão.
 *
 * Remove só o LOTE exibido, e não a fila inteira: uma conquista que caiu
 * enquanto o cartão estava aberto não foi vista por ninguém, e some se o
 * fechamento levar tudo junto.
 */
export function dequeueBadges(badgeIds: string[]): void {
  hydrate();
  const remaining = queue.filter((id) => !badgeIds.includes(id));
  const changed = remaining.length !== queue.length;
  queue = remaining;
  if (changed) writeArray(QUEUE_KEY, queue);
  // `markBadgesCelebrated` emite quando mexe na fila; aqui a fila já mudou,
  // então o emit é sempre nosso.
  markBadgesCelebrated(badgeIds);
  if (changed) emit();
}

export function subscribeBadgeQueue(listener: Listener): () => void {
  hydrate();
  listeners.add(listener);
  listener([...queue]);
  return () => {
    listeners.delete(listener);
  };
}

/** Só para testes — zera memória e espelho. */
export function __resetBadgeQueueForTests(): void {
  queue = [];
  celebrated = [];
  hydrated = false;
  listeners.clear();
  writeArray(QUEUE_KEY, []);
  writeArray(CELEBRATED_KEY, []);
}
